import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EnvelopeSimple, Plus, Trash, Star, ArrowsClockwise, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const inputCls = "px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60";

export default function ManagementEmails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/management-emails");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const primary = useMemo(() => items.find((e) => e.is_primary), [items]);

  const add = async (ev) => {
    ev.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/admin/management-emails", {
        email: newEmail.trim(),
        label: newLabel.trim(),
        is_primary: items.length === 0,
      });
      setItems((prev) => [...prev, data]);
      setNewEmail("");
      setNewLabel("");
      toast.success("Email added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const setPrimary = async (id) => {
    try {
      const { data } = await api.patch(`/admin/management-emails/${id}`, { is_primary: true });
      setItems((prev) => prev.map((e) => e.id === id ? data : { ...e, is_primary: false }));
      toast.success("Primary email updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const updateLabel = async (id, label) => {
    try {
      const { data } = await api.patch(`/admin/management-emails/${id}`, { label });
      setItems((prev) => prev.map((e) => e.id === id ? data : e));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this email?")) return;
    try {
      await api.delete(`/admin/management-emails/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Email removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copy = async (email) => {
    try { await navigator.clipboard.writeText(email); toast.success("Copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  return (
    <section data-testid="admin-management-emails">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Management Emails</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Where inquiries go.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            The <span className="text-neon-cyan font-mono">primary</span> address shows on the public contact form and is used for the auto-reply. Add more addresses as the team grows; promote any to primary in one click.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="mgmt-emails-reload">
          <ArrowsClockwise size={14} /> Reload
        </button>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="rounded-xl border border-ink-600 bg-ink-900/40 p-4 mb-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="mgmt-emails-add-form">
        <div className="sm:col-span-5">
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400 block mb-1.5">New email</label>
          <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="name@email.com" className={`${inputCls} w-full`} data-testid="mgmt-emails-new-email" />
        </div>
        <div className="sm:col-span-5">
          <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400 block mb-1.5">Label (optional)</label>
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="A&R lead, Booking, etc." className={`${inputCls} w-full`} data-testid="mgmt-emails-new-label" />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={adding} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-neon-cyan text-black text-xs font-semibold hover:bg-neon-cyanHover disabled:opacity-50" data-testid="mgmt-emails-add-btn">
            <Plus size={14} weight="bold" /> {adding ? "Adding..." : "Add"}
          </button>
        </div>
      </form>

      {/* List */}
      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center text-zinc-500 text-sm">No management emails configured. Add one above.</div>
        ) : (
          <ul>
            {items.map((item) => (
              <li key={item.id} className="border-b border-ink-700/60 last:border-0 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`mgmt-email-row-${item.id}`}>
                <EnvelopeSimple size={16} className={item.is_primary ? "text-neon-cyan" : "text-zinc-500"} weight={item.is_primary ? "fill" : "regular"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-mono break-all">{item.email}</span>
                    {item.is_primary && (
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-neon-cyan text-black font-bold" data-testid={`mgmt-email-${item.id}-primary-badge`}>
                        primary
                      </span>
                    )}
                  </div>
                  <input
                    defaultValue={item.label || ""}
                    onBlur={(e) => {
                      if (e.target.value.trim() !== (item.label || "")) updateLabel(item.id, e.target.value.trim());
                    }}
                    placeholder="Add a label…"
                    className="mt-1 w-full bg-transparent text-xs text-zinc-400 placeholder:text-zinc-600 focus:outline-none focus:text-zinc-200"
                    data-testid={`mgmt-email-${item.id}-label`}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => copy(item.email)} className="p-1.5 rounded border border-ink-600 text-zinc-400 hover:text-neon-cyan hover:border-neon-cyan/50" title="Copy email" data-testid={`mgmt-email-${item.id}-copy`}>
                    <Copy size={14} />
                  </button>
                  {!item.is_primary && (
                    <button onClick={() => setPrimary(item.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-ink-600 text-xs text-zinc-300 hover:text-amber-300 hover:border-amber-300/40" data-testid={`mgmt-email-${item.id}-make-primary`}>
                      <Star size={12} weight="fill" /> Make primary
                    </button>
                  )}
                  <button onClick={() => remove(item.id)} disabled={items.length === 1} className="p-1.5 rounded border border-ink-600 text-zinc-400 hover:text-neon-magenta hover:border-neon-magenta/50 disabled:opacity-30 disabled:cursor-not-allowed" title={items.length === 1 ? "Can't remove the last email" : "Remove"} data-testid={`mgmt-email-${item.id}-delete`}>
                    <Trash size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {primary && (
        <p className="mt-3 text-xs text-zinc-500 font-mono" data-testid="mgmt-emails-summary">
          Auto-reply will say: "…will reach back from <span className="text-neon-cyan">{primary.email}</span>".
        </p>
      )}
    </section>
  );
}
